import { throwError } from 'rxjs';
import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Observable, empty} from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { AuthService } from './auth.service';
import { Router } from '@angular/router';

export abstract class BaseService extends AuthService {

  constructor(protected http: HttpClient, private router: Router) { 
    super(http);
  }

  protected handleError(error: HttpErrorResponse) {
    // jwt token invalid or timed out - return to login page
    if ((error.status === 401 || error.status === 403) && (window.location.href.match(/\?/g) || []).length < 2) {
      AuthService.logout(this.router);
      return empty();
    }

    let errorMessage = '';

    if (error.error instanceof ErrorEvent) {
      // client-side error
      errorMessage = `Error: ${error.error.message}`;
    } else if (error.error && error.error.error_message != null) {
      // custom error
      errorMessage = `Error: ${error.error.error_message}`;
    } else {
      let modelError = '';
      if (typeof (error.error) === 'string' || error.error instanceof String) {
        modelError = error.error.toString();
      } else {
        for (let modelKey in error.error) {
          if (error.error[modelKey] == null) continue;
          let modelState = error.error[modelKey];

          if (Array.isArray(modelState) && modelState.length > 0) {
            modelError += modelState[0] + ' ';
          }
        }
      }

      if (modelError.indexOf('JWT Invalidated') > 0) {
        AuthService.logout(this.router);
        return empty();
      }

      // unhandled server-side error
      errorMessage = `Error: ${modelError} - Error Code: ${error.status} - Message: ${error.message}`;
    }

    return throwError(errorMessage || 'Server error');
  }

  protected httpGet<T>(actionUrl: string): Observable<any> {
    let headers = new HttpHeaders({
      "Content-Type": "application/json",
      "Authorization": `Bearer ${BaseService.authToken}`
    });

    let options = {
      headers: headers
    };

    return this.http.get<T>(this.apiUrl + actionUrl, options)
      .pipe(
        map((data: T) => { return data; }),
        catchError(err => this.handleError(err))
      );
  }

  protected httpGetFile(actionUrl: string): Observable<Blob> {
    let headers = new HttpHeaders({
      "Authorization": `Bearer ${BaseService.authToken}`
    });

    let options: Object = {
      headers: headers,
      responseType: 'blob'
    };

    return this.http.get<Blob>(this.apiUrl + actionUrl, options)
      .pipe(
        map((data: Blob) => { return data; }),
        catchError(err => this.handleError(err))
      );
  }

  protected httpDelete<T>(actionUrl: string): Observable<any> {
    let headers = new HttpHeaders({
      "Content-Type": "application/json",
      "Authorization": `Bearer ${BaseService.authToken}`
    });

    let options = {
      headers: headers
    };

    return this.http.delete<T>(this.apiUrl + actionUrl, options)
      .pipe(
        map((data: T) => { return data; }),
        catchError(err => this.handleError(err))
      );
  }

  protected httpPut<T>(actionUrl: string, params: Object): Observable<any> {
    let headers = new HttpHeaders({
      "Content-Type": "application/json",
      "Authorization": `Bearer ${BaseService.authToken}`
    });

    let options = {
      headers: headers
    };

    let requestBody = JSON.stringify(params);

    return this.http.put<T>(this.apiUrl + actionUrl, requestBody, options)
      .pipe(
        map((data: T) => { return data; }),
        catchError(err => this.handleError(err))
      );
  }

  protected httpPost<T>(actionUrl: string, params: Object): Observable<any> {
    let headers = new HttpHeaders({
      "Content-Type": "application/json",
      "Authorization": `Bearer ${BaseService.authToken}`
    });

    let options = {
      headers: headers
    };

    let requestBody = JSON.stringify(params);

    return this.http.post<T>(this.apiUrl + actionUrl, requestBody, options)
      .pipe(
        map((data: T) => { return data; }),
        catchError(err => this.handleError(err))
      );
  }

  protected httpPostFile(actionUrl: string, params: Object): Observable<Blob> {
    let headers = new HttpHeaders({
      "Content-Type": "application/json",
      "Authorization": `Bearer ${BaseService.authToken}`
    });

    let options: Object = {
      headers: headers,
      responseType: 'blob'
    };

    let requestBody = JSON.stringify(params);

    return this.http.post<Blob>(this.apiUrl + actionUrl, requestBody, options)
      .pipe(
        map((data: Blob) => { return data; }),
        catchError(err => this.handleError(err))
      );
  }
  
}
